import { ThemeCoreItem } from './ThemeCoreItem'
import { G_VERSION, G_SESSIONNAME } from './Const'

const isSSR = () => {
  return typeof window === 'undefined' || !window.document
}
interface CustomPath {
  [theme: string]: string
}

export class ThemeCoreMananager {
  items: Array<ThemeCoreItem>
  itemsRemoved: Array<ThemeCoreItem>
  corenames: Array<string>
  fallbackCores: { [core: string]: Array<string> }
  custompath: CustomPath
  loadstate: number
  theme: string
  cacheKey: string
  onLoaded?: (self?: ThemeCoreMananager) => void

  constructor() {
    this.corenames = []
    this.fallbackCores = {}
    this.theme = this.loadThemeLocalSetting('light-default') ?? 'light-default'
    this.loadstate = 0
    this.custompath = {}
    this.items = []
    this.itemsRemoved = []
    this.cacheKey = '1'
  }

  isSystemDarkMode() {
    if (!isSSR()) {
      return (
        window &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      )
    }
  }

  setCustomPath(theme: string, path: string) {
    this.custompath[theme] = path
    this.updateItems()
    this.update()
  }

  setDarkMode(darkmode: boolean) {
    if (this.theme.includes('light') && darkmode) {
      this.setTheme(this.theme.replace(/light/gi, 'dark'))
    } else if (this.theme.includes('dark') && !darkmode) {
      this.setTheme(this.theme.replace(/dark/gi, 'light'))
    }
    this.update()
  }

  isDark() {
    return this.theme?.includes('dark')
  }

  setTheme(theme: string) {
    if (!theme.includes('-')) {
      theme = `${theme}-default`
    }
    if (this.theme != theme) {
      if (this.loadstate == 1) {
        this.logErr('Theme is busy loading')
        return
      }
      this.theme = theme
      this.updateItems()
      this.update()
    }
  }

  logErr(msg: string) {
    if (!isSSR()) {
      if (document['DEBUG']) {
        console.log(
          `☢☢ ThemeCore ☢☢ (${msg}) Theme:  ${this.theme} Cores: ${this.corenames}`
        )
      }
    }
  }

  addCoreNames(
    cores: Array<string> | string,
    fallbackCores?: Array<string>
  ): boolean | void {
    let names: Array<string> = []
    if (Array.isArray(cores)) {
      names = cores
    } else {
      names = cores.split(',')
    }
    const newcores = names.filter(
      (val) =>
        !this.corenames.find((cv) => cv.toLowerCase() == val.toLowerCase())
    )

    fallbackCores?.forEach((c, i) => {
      if (names[i]) {
        this.fallbackCores[names[i]] = [
          ...(this.fallbackCores[names[i]] ?? []),
          c,
        ]
      }
    })

    // console.log(
    //   'newcores',
    //   names,
    //   fallbackCores,
    //   'fallbackCores',
    //   this.fallbackCores
    // )
    if (newcores.length > 0) {
      this.setCoreNames([...this.corenames, ...newcores])
      return true
    }
    return false
  }

  hasCore(core: string): boolean {
    const coreItem = this.items.filter(
      (val) => val.core == core || val.defaultCore == core
    )?.[0]
    return coreItem !== undefined && coreItem !== null
  }

  coreStatus(core: string): string {
    const coreItem = this.items.filter(
      (val) => val.core == core || val.defaultCore == core
    )?.[0]
    return coreItem?.loadstatus ?? ''
  }

  setCoreNames(cores: Array<string> | string) {
    let names: Array<string> = []
    if (Array.isArray(cores)) {
      names = cores
    } else {
      names = cores.split(',')
    }
    names = names
      .filter((val) => val ?? '' != '')
      .map((val) => {
        if (String(val).includes('.core')) return String(val)
        return `${val}.core`
      })

    const newcores = names.filter(
      (val) =>
        !this.corenames.find((cv) => cv.toLowerCase() == val.toLowerCase())
    )
    const missingcores = this.corenames.filter(
      (val) => !names.find((cv) => cv.toLowerCase() == val.toLowerCase())
    )

    if (newcores.length > 0 || missingcores.length > 0) {
      this.corenames = names
      this.updateItems()
    }
  }

  loadThemeLocalSetting = (def: string): string => {
    if (!isSSR()) {
      try {
        const val = window.localStorage.getItem(G_SESSIONNAME)
        if (val) {
          return val
        }
        if (def) {
          return def
        }
      } catch (e) {
        this.logErr(`Failed to load local setting ${G_SESSIONNAME}`)
      }
      return this.theme
    }
  }

  saveThemeLocalSetting = (value: string): void => {
    if (!isSSR()) {
      try {
        window.localStorage.setItem(G_SESSIONNAME, value)
      } catch (e) {
        this.logErr(`Failed to save local setting ${G_SESSIONNAME}`)
      }
    }
  }

  async loadItemCallback(item?: ThemeCoreItem, retval?: number, msg?: string) {
    //console.log(`Load items callback`, item, retval, msg)
  }

  update() {
    if (!isSSR()) {
      const evt = new CustomEvent('themecore.update', {
        detail: {
          loaded: this.loadstate == 2 ? true : false,
          manager: this,
          loadstate: this.loadstate,
          theme: this.theme,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })

      window.document.dispatchEvent(evt)
    }
  }

  async updateItems() {
    if (!isSSR()) {
      const newitems: Array<ThemeCoreItem> = []
      const promises: Array<Promise<ThemeCoreItem>> = []

      this.loadstate = 1
      this.itemsRemoved = this.items.filter((val) => {
        if (
          !(
            this.corenames.includes(val.core) &&
            this.corenames.includes(val.defaultCore)
          )
        ) {
          return true
        }
        if (this.theme != val.theme) {
          return true
        }

        if (val.loadstatus == 'failed') {
          return true
        }
      })

      const founditems = this.items.filter(
        (val) => !this.itemsRemoved.includes(val)
      )

      const foundcores = founditems.map((val) => val.core)

      //Add new items
      for (let core of this.corenames) {
        if (core == '') continue
        if (foundcores.filter((coreval) => coreval == core).length > 0) {
          continue
        }
        let item = new ThemeCoreItem(
          core,
          this.theme,
          this.fallbackCores?.[core]
        )
        item.cacheKey = this.cacheKey
        item.updatePath()
        item.loadCallback = (retval: number, msg?: string) =>
          this.loadItemCallback(item, retval, msg ?? '')

        newitems.push(item)
        promises.push(item.append())
      }

      //console.log('Core update', newitems, this.itemsRemoved, foundcores, this)

      //Remove old items
      this.itemsRemoved.forEach((item) => {
        promises.push(item.remove())
      })

      const oldItems = this.items
      this.items = [...founditems, ...newitems]
      this.saveThemeLocalSetting(this.theme)

      this.update()

      Promise.all(promises)

        .catch((errlist) => {
          this.logErr(`Cores Errors ${JSON.stringify(errlist)}`)
        })
        .finally(() => {
          //this.loadItemCallback(undefined, 0, '')
          this.loadstate = 2
          this.itemsRemoved = []
          this.onLoaded?.(this)
          //console.log('!!!!!!!! Loaded', this)

          const evt = new CustomEvent('themecore.loaded', {
            detail: {
              loaded: this.loadstate == 2 ? true : false,
              manager: this,
              loadstate: this.loadstate,
            },
            bubbles: true,
            cancelable: false,
            composed: false,
          })

          window.dispatchEvent(evt)
        })
    }
  }
}

export const GloThemeManager = new ThemeCoreMananager()
export const getGloThemeManager = () => GloThemeManager

declare global {
  interface Window {
    GloThemeManager: Array<any>
  }
}

if (!isSSR()) {
  if (!window.GloThemeManager) window.GloThemeManager = []
  window.GloThemeManager.push(GloThemeManager)
}

/**
 * Loads the core required by a component.
 * @param corename [String or Array of strings] Name of theme core. e.g. page.core, wijmo.core, syncfusion.core
 * @returns
 */
export const requireCore = (corename: string | Array<string>) => {
  return GloThemeManager.addCoreNames(corename)
}

export const getState = (): ThemeCoreMananager => {
  return GloThemeManager
}

export const setTheme = (theme: string) => {
  return GloThemeManager.setTheme(theme)
}

export const setDarkMode = (on: boolean) => {
  return GloThemeManager.setDarkMode(on)
}

export const getTheme = (): string => {
  return GloThemeManager.theme
}
